import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, CheckCircle2, BadgeDollarSign, Files, CircuitBoard, BadgeCheck, ClipboardList, NotebookPen, Truck } from "lucide-react";
import api, { apiError } from "@/lib/api";
import DocPreview from "@/components/DocPreview";
import StatusBadge from "@/components/StatusBadge";
import PaymentStatusChip from "@/components/PaymentStatusChip";
import NotesTab from "@/pages/tabs/NotesTab";
import { fmtAUD, fmtAUDex, fmtDate, fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

function Info({ label, value, testId, wide = false }) {
  return (
    <div className={`border-t border-gray-200 pt-3 ${wide ? "sm:col-span-2" : ""}`}>
      <div className="bdx-label">{label}</div>
      <div data-testid={testId} className="mt-1.5 text-sm font-medium leading-relaxed text-gray-900">{value || "—"}</div>
    </div>
  );
}

const ExAmt = ({ v }) => (
  <span className="whitespace-nowrap">{fmtAUDex(v)}<span className="ml-0.5 align-baseline text-[0.62em] font-semibold text-gray-500">excl</span></span>
);

function FinancialMetric({ label, value, sub, testId, emphasis = false, divider = false, valueClass }) {  return (
    <div className={`border-t border-gray-200 pt-4 lg:border-t-0 lg:pt-0 ${divider ? "lg:border-l lg:border-gray-200 lg:pl-5" : ""}`}>
      <div className="text-[9px] font-semibold uppercase text-gray-500" style={{ letterSpacing: "0.12em" }}>{label}</div>
      <div data-testid={testId} className={`mt-2 font-mono-data ${emphasis ? "text-xl font-semibold" : "text-lg font-medium"} ${valueClass || (emphasis ? "text-gray-950" : "text-gray-800")}`}>{value}</div>
      {sub && <div className="mt-1 font-mono-data text-[11px] text-gray-500">{sub}</div>}
    </div>
  );
}

function SectionTitle({ icon: Icon, children, detail }) {
  return (
    <div className="mb-5 flex items-start justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#d9cde5] bg-[#f3eff7] text-[#604184] shadow-sm"><Icon size={16} strokeWidth={1.8} /></div>
        <h2 className="font-heading text-base font-bold text-gray-950">{children}</h2>
      </div>
      {detail && <span className="hidden text-[10px] font-medium text-gray-400 sm:block">{detail}</span>}
    </div>
  );
}

function ModuleStatusSection({ project, refresh, readOnly }) {
  const ml = project.module_landing || {};
  const [expDate, setExpDate] = useState(ml.expected_date || "");
  const [landDate, setLandDate] = useState(ml.landed_date || new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  const patch = async (payload, msg) => {
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}/module-status`, payload);
      toast.success(msg);
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <section className="rounded-lg border border-gray-200 bg-white px-5 py-5 shadow-sm" data-testid="module-status-section">
      <SectionTitle icon={Truck}>Module Status</SectionTitle>
      <div className="mb-4">
        {ml.landed ? (
          <span data-testid="ov-module-status-chip" className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[10px] font-semibold uppercase text-emerald-700" style={{ letterSpacing: "0.06em" }}>
            Landed{ml.landed_date ? ` · ${fmtDate(ml.landed_date)}` : ""}
          </span>
        ) : (
          <span data-testid="ov-module-status-chip" className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-[10px] font-semibold uppercase text-sky-700" style={{ letterSpacing: "0.06em" }}>
            Landing{ml.expected_date ? ` · ${fmtDate(ml.expected_date)}` : ""}
          </span>
        )}
      </div>
      <dl className="divide-y divide-gray-200 text-sm">
        <div className="flex items-center justify-between gap-4 py-3 first:pt-0">
          <dt className="text-gray-500">Expected Landing Date</dt>
          <dd data-testid="ov-module-expected-date" className="font-mono-data font-semibold text-gray-950">{ml.expected_date ? fmtDate(ml.expected_date) : "—"}</dd>
        </div>
        <div className="flex items-center justify-between gap-4 py-3 last:pb-0">
          <dt className="text-gray-500">Actual Landed Date</dt>
          <dd data-testid="ov-module-landed-date" className="font-mono-data font-semibold text-gray-950">{ml.landed && ml.landed_date ? fmtDate(ml.landed_date) : "—"}</dd>
        </div>
      </dl>
      {!readOnly && ml.landed && (
        <div className="mt-4 space-y-2 border-t border-gray-200 pt-4">
          <div className="bdx-label mb-1.5">Correct Actual Landed Date</div>
          <div className="flex gap-2">
            <input data-testid="ov-module-landed-edit-input" type="date" className="bdx-input" value={landDate} onChange={(e) => setLandDate(e.target.value)} />
            <button data-testid="ov-module-landed-edit-save" disabled={busy || !landDate} className="bdx-btn-secondary shrink-0"
              onClick={() => patch({ landed_date: landDate }, "Actual Landed Date corrected")}>Save</button>
          </div>
          <p className="text-[11px] leading-relaxed text-gray-500">Data-entry correction only — the module remains LANDED and no other project data changes.</p>
        </div>
      )}
      {!readOnly && !ml.landed && (
        <div className="mt-4 space-y-3 border-t border-gray-200 pt-4">
          <div>
            <div className="bdx-label mb-1.5">Expected Landing Date</div>
            <div className="flex gap-2">
              <input data-testid="ov-module-expected-input" type="date" className="bdx-input" value={expDate} onChange={(e) => setExpDate(e.target.value)} />
              <button data-testid="ov-module-expected-save" disabled={busy} className="bdx-btn-secondary shrink-0"
                onClick={() => patch({ expected_date: expDate || null }, "Expected landing date saved")}>Save</button>
            </div>
          </div>
          <div>
            <div className="bdx-label mb-1.5">Mark Module Landed</div>
            <div className="flex gap-2">
              <input data-testid="ov-module-landed-input" type="date" className="bdx-input" value={landDate} onChange={(e) => setLandDate(e.target.value)} />
              <button data-testid="ov-module-landed-btn" disabled={busy} className="bdx-btn-primary shrink-0"
                onClick={() => patch({ landed: true, landed_date: landDate || null }, "Module marked as LANDED")}>Landed</button>
            </div>
          </div>
          <p className="text-[11px] leading-relaxed text-gray-500">Physical module delivery milestone only — it does not complete, archive or affect roadmaps, claims or financials.</p>
        </div>
      )}
    </section>
  );
}

function QuotationValueCorrection({ project, refresh }) {
  const [open, setOpen] = useState(false);
  const [exGst, setExGst] = useState("");
  const [busy, setBusy] = useState(false);
  const inc = Math.round((parseFloat(exGst || 0) || 0) * 1.1 * 100) / 100;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}/quotation-value`, { value_ex_gst: parseFloat(exGst || 0) });
      toast.success(`Current quotation value corrected — Base Contract now ${fmtAUD(inc)} GST Inc`);
      setOpen(false);
      setExGst("");
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button data-testid="quotation-correct-btn" className="bdx-btn-secondary mt-2 w-full justify-center text-xs" onClick={() => setOpen(true)}>
        Correct Quotation Value
      </button>
    );
  }
  return (
    <div className="mt-3 rounded-md border border-[#d9cde5] bg-[#faf8fc] px-3 py-3" data-testid="quotation-correct-form">
      <div className="bdx-label mb-1.5">Corrected Quotation Value (Excluding GST)</div>
      <input data-testid="quotation-correct-input" type="number" step="0.01" min="0" className="bdx-input font-mono-data bg-white" value={exGst} onChange={(e) => setExGst(e.target.value)} placeholder="e.g. 100000" autoFocus />
      <div className="mt-2 flex items-center justify-between text-[11px]">
        <span className="text-gray-500">Contract Value (GST Inclusive) — × 1.1</span>
        <span className="font-mono-data font-semibold text-gray-700" data-testid="quotation-correct-inc">{fmtAUD(inc)}</span>
      </div>
      <p className="mt-2 text-[10px] leading-relaxed text-gray-500">Corrects the authoritative Base Contract value of the current revision only. The quotation PDF and superseded revisions are unchanged.</p>
      <div className="mt-3 flex justify-end gap-2">
        <button data-testid="quotation-correct-cancel" className="bdx-btn-secondary text-xs" onClick={() => { setOpen(false); setExGst(""); }}>Cancel</button>
        <button data-testid="quotation-correct-save" className="bdx-btn-primary text-xs" disabled={busy || exGst === ""} onClick={save}>{busy ? "Saving…" : "Save Correction"}</button>
      </div>
    </div>
  );
}

export default function OverviewTab({ project, refresh }) {
  const { readOnly } = useAuth();
  const [fin, setFin] = useState(null);
  const [variations, setVariations] = useState([]);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get(`/projects/${project.id}/financials`).then((r) => setFin(r.data));
    api.get(`/projects/${project.id}/variations`).then((r) => setVariations(r.data));
  }, [project.id, project.updated_at]);

  const quotations = [...(project.quotations || [])].reverse();
  const currentRev = (project.quotations || []).length;
  const unclaimedApproved = variations.filter((v) => v.status === "Approved" && !v.claimed);
  const canComplete = fin && fin.base_contract_claimed_pct >= 100 && unclaimedApproved.length === 0;
  const completeBlockReason = !fin ? "" :
    fin.base_contract_claimed_pct < 100
      ? `Project cannot be completed. Base Contract is ${fin.base_contract_claimed_pct}% claimed.`
      : unclaimedApproved.length > 0
        ? `Project cannot be completed. ${unclaimedApproved.map((v) => v.code).join(", ")} ${unclaimedApproved.length === 1 ? "remains" : "remain"} unclaimed.`
        : "";

  const complete = async () => {
    if (!window.confirm(`Mark project ${project.project_number} as Completed? It will move to the Completed / Archived ledger.`)) return;
    try {
      await api.post(`/projects/${project.id}/complete`);
      toast.success("Project marked as completed and moved to Completed / Archived");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const reopen = async () => {
    if (!window.confirm(`Unmark project ${project.project_number} as complete? It will return to the active register with the same project number, tranche, financials, claims, documents and history intact.`)) return;
    try {
      await api.post(`/projects/${project.id}/reopen`);
      toast.success("Project unmarked as complete — restored to the active register");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="overview-tab" className="bdx-stagger">
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0_4px_16px_rgba(16,19,21,0.06)] xl:col-span-8" data-testid="financial-position">
        <div className="h-1 bg-[#765297]" />
        <div className="flex flex-col gap-4 border-b border-gray-200 bg-[#faf9f8] px-6 py-4 sm:flex-row sm:items-center sm:justify-between lg:px-7">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#d9cde5] bg-[#f3eff7] text-[#604184] shadow-sm"><BadgeDollarSign size={18} strokeWidth={1.8} /></div>
            <div>
              <h2 className="font-heading text-lg font-bold text-gray-950">Financial Position</h2>
              <p className="mt-0.5 text-xs text-gray-500">Approved project financial summary</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-6 lg:px-7 lg:py-7">
          <div data-testid="ov-contract-position">
            <div className="mb-4 text-[10px] font-bold uppercase text-[#604184]" style={{ letterSpacing: "0.14em" }}>Contract Position</div>
            <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-3">
              <FinancialMetric label="Current Base Contract" value={<ExAmt v={project.base_contract_value} />} testId="ov-base-value" />
              <FinancialMetric label="Approved Variations" value={<ExAmt v={fin ? fin.approved_variations_total : project.approved_variations_total} />} testId="ov-approved-variations" divider />
              <FinancialMetric label="Approved Contract Total" value={<ExAmt v={fin ? fin.current_approved_contract_total : project.current_contract_sum} />} testId="ov-contract-total" emphasis divider />
            </div>
          </div>
          <div className="my-6 border-t border-gray-200 lg:my-7" />
          <div data-testid="ov-claim-position">
            <div className="mb-4 flex items-center gap-3">
              <div className="text-[10px] font-bold uppercase text-[#604184]" style={{ letterSpacing: "0.14em" }}>Claim Position</div>
              {fin && <PaymentStatusChip status={fin.payment_status} testId="ov-payment-status" />}
            </div>
            <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-3">
              <FinancialMetric label="Base Contract Claimed" value={fin ? <ExAmt v={fin.base_contract_claimed_value} /> : "—"} sub={fin ? `${fin.base_contract_claimed_pct}% of base contract` : null} testId="ov-base-claimed" valueClass="text-blue-800" />
              <FinancialMetric label="Variations Claimed" value={fin ? <ExAmt v={fin.variations_claimed_total} /> : "—"} sub={fin ? `${fin.variations_claimed_count} of ${fin.approved_variations_count} approved claimed` : null} testId="ov-variations-claimed" divider valueClass="text-blue-800" />
              <FinancialMetric label="Remaining" value={fin ? <ExAmt v={fin.remaining_contract_value} /> : "—"} testId="ov-remaining-value" emphasis divider valueClass="text-red-700" />
            </div>
          </div>
          <p className="mt-6 border-t border-gray-100 pt-3 text-[11px] text-gray-500" data-testid="ov-gst-footnote">All financial values shown are exclusive of GST.</p>
        </div>
      </section>

      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0_4px_16px_rgba(16,19,21,0.06)] xl:col-span-4" data-testid="ov-quotation-panel">
        <div className="h-1 bg-[#765297]" />
        <div className="flex items-center gap-3 border-b border-gray-200 bg-[#faf9f8] px-6 py-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#d9cde5] bg-[#f3eff7] text-[#604184] shadow-sm"><Files size={18} strokeWidth={1.8} /></div>
          <div>
            <h2 className="font-heading text-lg font-bold text-gray-950">Quotation Documents</h2>
            <p className="mt-0.5 text-xs text-gray-500">Contract price reference</p>
          </div>
        </div>
        <div className="px-6 py-5">
        {quotations.length === 0 && <p data-testid="quotation-empty-state" className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-5 text-sm leading-relaxed text-gray-500">No quotation uploaded yet — add one via the ledger row menu → Edit Project.</p>}
        <div className="space-y-3" data-testid="quotation-list">
          {quotations.map((q, i) => (
            <div key={q.revision} className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3.5">
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm font-semibold text-gray-950">Quotation — Revision {q.revision}</div>
                <StatusBadge status={i === 0 ? "Current" : "Superseded"} tone={i === 0 ? "green" : "grey"} />
              </div>
              <div className="mt-2 break-all font-mono-data text-[11px] text-gray-500">{q.filename}</div>
              <div className="mt-1 font-mono-data text-[11px] text-gray-600" data-testid={`quotation-value-rev${q.revision}`}>{fmtAUDex(q.value)} Excl. GST{q.quotation_date ? ` · ${fmtDate(q.quotation_date)}` : ""}</div>
              <div className="mt-0.5 font-mono-data text-[10px] text-gray-400">Uploaded {fmtDate(q.uploaded_at)} by {q.uploaded_by}</div>
              <button data-testid={`quotation-view-rev${q.revision}`}
                className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded border border-blue-300 bg-blue-50 px-3 py-2 text-[11px] font-bold uppercase text-blue-700 shadow-sm transition-colors hover:border-blue-400 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-300"
                style={{ letterSpacing: "0.06em" }}
                title={`View ${q.filename}`}
                onClick={() => setPreview({ id: q.doc_id, filename: q.filename, content_type: "application/pdf" })}>
                <Eye size={13} strokeWidth={1.8} /> View Document
              </button>
              {i === 0 && !readOnly && <QuotationValueCorrection project={project} refresh={refresh} />}
            </div>
          ))}
        </div>
        </div>
      </section>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-12">
        <div className="space-y-6 xl:col-span-8">
          <section className="rounded-lg border border-gray-200 bg-white px-6 py-6 shadow-sm lg:px-7">
            <SectionTitle icon={ClipboardList} detail="Edit via the ledger row menu → Edit Project">Project Information</SectionTitle>
            <div className="grid grid-cols-1 gap-x-7 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
              <Info label="Project Number" value={project.display_reference || project.project_number} testId="ov-number" />
              <Info label="Tranche Allocation" value={project.tranche ? `Tranche ${project.tranche} (T${project.tranche})` : "No tranche"} testId="ov-tranche" />
              <Info label="Project Type" value={project.project_type === "SSH" ? "SSH — Social Supported Housing" : "MMC — Modern Methods of Construction"} />
              <Info label="Status" value={<StatusBadge status={project.status} />} />
              <Info label="Address" value={project.label} testId="ov-address" wide />
              <Info label="Distributor" value={project.distributor} />
              <Info label="Supply Type" value={project.supply_type} />
              <Info label="Contract Administrator (BLG)" value={project.contract_admin} testId="ov-contract-admin" />
              <Info label="Contract Administrator Email" value={project.contract_admin_email} testId="ov-contract-admin-email" />
              <Info label="Quotation Revision" value={currentRev > 0 ? `Revision ${currentRev} — Current` : "No quotation uploaded"} testId="ov-quotation-rev" />
              <Info label="Created" value={`${fmtDate(project.created_at)} · ${project.created_by}`} />
              <Info label="Dwellings" value={`${project.dwellings_connected} of ${project.dwellings_required ?? project.dwellings_total} Connected${(project.dwellings_waived || 0) > 0 ? ` · ${project.dwellings_waived} by others` : ""}`} testId="ov-dwellings" />
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white px-6 py-6 shadow-sm lg:px-7">
            <SectionTitle icon={NotebookPen}>Project Notes / Diary</SectionTitle>
            <NotesTab project={project} />
          </section>
        </div>

        <aside className="space-y-5 xl:col-span-4">
          <section className="rounded-lg border border-gray-200 bg-white px-5 py-5 shadow-sm">
            <SectionTitle icon={CircuitBoard}>Current Electrical Status</SectionTitle>
            <dl className="divide-y divide-gray-200 text-sm">
              <div className="flex items-center justify-between gap-4 py-3 first:pt-0"><dt className="text-gray-500">Dwellings Connected</dt><dd data-testid="ov-electrical-dwellings" className="font-mono-data font-semibold text-gray-950">{project.dwellings_connected} of {project.dwellings_required ?? project.dwellings_total}{(project.dwellings_waived || 0) > 0 && <span className="ml-1.5 text-[10px] font-medium text-sky-700">+{project.dwellings_waived} by others</span>}</dd></div>
              <div className="flex items-center justify-between gap-4 py-3"><dt className="text-gray-500">Electrical Works</dt><dd data-testid="ov-electrical-works" className="font-semibold text-gray-900">{project.works_complete ? "Completed" : "In Progress"}</dd></div>
              <div className="flex items-center justify-between gap-4 py-3"><dt className="text-gray-500">Group Meter Panel CES</dt><dd data-testid="ov-electrical-gmp" className="font-semibold text-gray-900">{project.electrical.gmp_ces?.status || "Not Started"}</dd></div>
              {project.supply_type === "Underground" && (
                <div className="flex items-center justify-between gap-4 py-3"><dt className="text-gray-500">Pit Application</dt><dd data-testid="ov-electrical-pit" className="font-semibold text-gray-900">{project.pit_overdue ? "Overdue" : project.electrical.pit_application.status}</dd></div>
              )}
              <div className="flex items-center justify-between gap-4 py-3 last:pb-0"><dt className="text-gray-500">NBN Pipe Stringing</dt><dd data-testid="ov-nbn-pipe" className="font-semibold text-gray-900">{project.nbn.pipe_stringing}</dd></div>
            </dl>
          </section>

          <ModuleStatusSection project={project} refresh={refresh} readOnly={readOnly} />

          {!readOnly && (
            <section className="rounded-lg border border-gray-200 bg-white px-5 py-5 shadow-sm">
              <SectionTitle icon={BadgeCheck}>Project Completion</SectionTitle>
              {project.status === "Completed" ? (
                <>
                  <div data-testid="ov-completed-state" className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm font-medium text-emerald-700">
                    <CheckCircle2 size={16} strokeWidth={1.5} /> Completed {project.completed_at ? fmtDateTime(project.completed_at) : ""}
                  </div>
                  <button data-testid="ov-reopen-btn" className="bdx-btn-secondary mt-3 w-full justify-center" onClick={reopen}>
                    Unmark as Complete
                  </button>
                  <p className="mt-2 text-[11px] leading-relaxed text-gray-500">Restores the project to the active register with the same number and all history preserved. Normal completion gating applies again before it can be re-completed.</p>
                </>
              ) : (
                <>
                  <button data-testid="ov-complete" className="bdx-btn-primary w-full justify-center" disabled={!canComplete} onClick={complete}>
                    Mark Project Completed
                  </button>
                  {!canComplete && completeBlockReason && (
                    <p data-testid="ov-complete-block-reason" className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-800">{completeBlockReason}</p>
                  )}
                  <p className="mt-3 text-[11px] leading-relaxed text-gray-500">Requires Base Contract 100% claimed and all Approved Variations claimed. Completed projects automatically move to Completed / Archived.</p>
                </>
              )}
            </section>
          )}
        </aside>
      </div>

      {preview && <DocPreview doc={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}